import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Header, Text } from 'components';
import MessageItem from 'components/Chat/MessageItem';
import TypingBubble from 'components/Chat/TypingBubble';
import {
  routes,
  YARIKANO_CHAT_ICON_URL,
  YARIKANO_CHAT_TITLE,
} from '../../constants';
import { useUserStore } from 'store';
import { createOperatorMember, createQuestionMessage, noop } from './helpers';
import useYarikanoChat from './useYarikanoChat';
import YarikanoChatFooter from './YarikanoChatFooter';
import YarikanoChatLogEntry from './YarikanoChatLogEntry';

function YarikanoChat() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUserStore();
  const operatorMember = useMemo(() => createOperatorMember(), []);

  const handleBack = () => {
    if (
      (location.state as { yarikanoEntry?: string } | null)?.yarikanoEntry ===
      'message-list'
    ) {
      navigate(-1);
      return;
    }
    navigate(routes.MESSAGE, { replace: true });
  };

  const {
    session,
    pendingQuestion,
    isResponding,
    remainingChips,
    showFooterCta,
    scrollContainerRef,
    bottomRef,
    handleChipSelect,
    handleCtaClick,
    footerQuestionId,
  } = useYarikanoChat(user?.id);

  return (
    <div
      className="fixed inset-0 flex flex-col overflow-hidden bg-white"
      data-testid="yarikano-chat-page"
    >
      <Header
        onBack={handleBack}
        title={
          <div className="max-w-[80%] flex gap-2 justify-center items-center w-full">
            <Avatar circle url={YARIKANO_CHAT_ICON_URL} size="w-9" />
            <Text className="text-ellipsis overflow-hidden max-w-[75%]" nowrap>
              {YARIKANO_CHAT_TITLE}
            </Text>
          </div>
        }
      />

      <div
        ref={scrollContainerRef}
        id="yarikanoChatScroll"
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4"
      >
        <div className="flex flex-col gap-4">
          {session.log.map((entry, index) => (
            <YarikanoChatLogEntry
              key={entry.id}
              entry={entry}
              index={index}
              log={session.log}
              operatorMember={operatorMember}
              onCtaClick={handleCtaClick}
            />
          ))}

          {pendingQuestion && (
            <div className="shrink-0">
              <MessageItem
                message={createQuestionMessage(
                  pendingQuestion.text,
                  pendingQuestion.timetoken,
                )}
                displayText={pendingQuestion.text}
                isEdited={false}
                isMyMessage
                isAdminMessage={false}
                showAvatar={false}
                onClickAvatar={noop}
              />
            </div>
          )}

          {isResponding && <TypingBubble avatarUrl={YARIKANO_CHAT_ICON_URL} />}

          <div ref={bottomRef} />
        </div>
      </div>

      <YarikanoChatFooter
        remainingChips={remainingChips}
        showFooterCta={showFooterCta}
        isResponding={isResponding}
        footerQuestionId={footerQuestionId}
        onChipSelect={handleChipSelect}
        onCtaClick={handleCtaClick}
      />
    </div>
  );
}

export default YarikanoChat;
